import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  LinearProgress,
  Alert,
  Chip,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Avatar,
  Tooltip,
} from '@mui/material';
import {
  MdSearch,
  MdRefresh,
  MdInfoOutline,
  MdFileDownload,
  MdGridOn,
  MdPrint,
  MdInventory,
  MdCurrencyRupee,
  MdWarningAmber,
  MdRemoveShoppingCart,
  MdEventBusy,
  MdHotel,
  MdAddShoppingCart,
  MdSyncProblem,
} from 'react-icons/md';
import { getData } from '../../Webservice/Webservice';
import {
  FONT,
  inr,
  fmtDate,
  toISO,
  authToken,
  StatCard,
  statGridSx,
  exportButtonSx,
  downloadCsv,
  downloadExcel,
  openPrintView,
} from './reportShared';

const REPORT_ROUTE = 'reports/inventory-report';
const pill = (bg, fg) => ({ fontWeight: 700, fontFamily: FONT, background: bg, color: fg, height: 24, fontSize: 11 });
const dash = <Typography component="span" sx={{ color: '#94a3b8' }}>—</Typography>;
const STATUS = {
  healthy: ['#dcfce7', '#166534', 'Healthy'],
  low: ['#fef3c7', '#92400e', 'Low stock'],
  out: ['#fee2e2', '#991b1b', 'Out of stock'],
  over: ['#e0e7ff', '#3730a3', 'Overstock'],
};
const statusChip = (s) => {
  const [bg, fg, text] = STATUS[s] || STATUS.healthy;
  return <Chip size="small" label={text} sx={pill(bg, fg)} />;
};
const expiryChip = (d) => {
  if (d === null || d === undefined) return dash;
  if (d < 0) return <Chip size="small" label={`Expired ${-d}d ago`} sx={pill('#1f2937', '#fff')} />;
  const [bg, fg] = d <= 30 ? ['#fee2e2', '#b91c1c'] : d <= 90 ? ['#ffedd5', '#9a3412'] : ['#dcfce7', '#166534'];
  return <Chip size="small" label={`${d} days`} sx={pill(bg, fg)} />;
};
const coverChip = (days, onHand) => {
  if (onHand <= 0) return <Chip size="small" label="None" sx={pill('#fee2e2', '#b91c1c')} />;
  if (days === null || days === undefined) return <Chip size="small" label="No sales" sx={pill('#f1f5f9', '#64748b')} />;
  const [bg, fg] = days < 7 ? ['#fee2e2', '#b91c1c'] : days < 15 ? ['#fef3c7', '#92400e'] : days > 180 ? ['#e0e7ff', '#3730a3'] : ['#dcfce7', '#166534'];
  return <Chip size="small" label={`${days} days`} sx={pill(bg, fg)} />;
};

const ProductCell = ({ r }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 200 }}>
    <Avatar variant="rounded" src={r.image || undefined} alt={r.product} sx={{ width: 36, height: 36, bgcolor: '#e2e8f0', color: '#475569', fontWeight: 700, fontSize: 12.5 }}>
      {(r.product || '?').charAt(0).toUpperCase()}
    </Avatar>
    <Box sx={{ lineHeight: 1.25, minWidth: 0 }}>
      <Typography sx={{ fontSize: 11.5, fontWeight: 700, fontFamily: FONT, maxWidth: 230, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.product}</Typography>
      <Typography sx={{ fontSize: 11.5, color: '#64748b', fontFamily: FONT }}>{[r.brand, r.productCode].filter(Boolean).join(' · ') || '—'}</Typography>
    </Box>
  </Box>
);

const col = (key, lbl, opts = {}) => ({ key, label: lbl, ...opts });
const money = (key, lbl, extra = {}) => col(key, lbl, { numeric: true, text: (r) => r[key], cell: (r) => inr(r[key]), print: (r) => inr(r[key]), ...extra });
const num = (key, lbl, extra = {}) => col(key, lbl, { numeric: true, text: (r) => r[key] ?? '', ...extra });
const productCol = col('product', 'Product', { text: (r) => r.product, cell: (r) => <ProductCell r={r} />, extra: (r) => ({ Code: r.productCode, Brand: r.brand, Category: r.category || '' }) });
const dateCol = (key, lbl) => col(key, lbl, { text: (r) => (r[key] ? fmtDate(r[key]) : ''), cell: (r) => (r[key] ? fmtDate(r[key]) : dash) });
const onHandCol = num('onHand', 'On Hand', { bold: true });
const coverCol = col('coverDays', 'Days of Cover', { center: true, text: (r) => (r.onHand <= 0 ? 0 : r.coverDays ?? ''), cell: (r) => coverChip(r.coverDays, r.onHand) });

const TABS = [
  {
    key: 'summary',
    label: 'Stock Summary',
    defaultSort: 'value_desc',
    sorts: [['value_desc', 'Stock value: high to low'], ['units_desc', 'On hand: high to low'], ['cover_asc', 'Days of cover: lowest first'], ['sold_desc', 'Sold (30d): high to low'], ['mismatch_desc', 'Largest mismatch first'], ['name_asc', 'Name: A to Z']],
    columns: [
      productCol,
      col('category', 'Category', { text: (r) => r.category }),
      onHandCol,
      num('systemQty', 'System Qty', {
        cell: (r) =>
          r.mismatch ? (
            <Tooltip title={`Storefront counter is ${r.mismatch > 0 ? 'higher' : 'lower'} than batch stock by ${Math.abs(r.mismatch)}`} arrow>
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: '#b91c1c', fontWeight: 800 }}>
                <MdWarningAmber size={15} /> {r.systemQty}
              </Box>
            </Tooltip>
          ) : (
            r.systemQty
          ),
        extra: (r) => ({ Mismatch: r.mismatch }),
      }),
      num('minStock', 'Min Stock'),
      col('status', 'Status', { text: (r) => STATUS[r.status]?.[2], cell: (r) => statusChip(r.status) }),
      money('costValue', 'Stock Value (Cost)', { bold: true }),
      money('mrpValue', 'Value at MRP'),
      num('sold30', 'Sold (30d)'),
      coverCol,
      num('batches', 'Batches', { extra: (r) => ({ Warehouses: r.warehouses }) }),
      col('expiring', 'Expiring', {
        center: true,
        text: (r) => `${r.expiringUnits}${r.expiredUnits ? ` (+${r.expiredUnits} expired)` : ''}`,
        cell: (r) =>
          r.expiredUnits ? <Chip size="small" label={`${r.expiredUnits} expired`} sx={pill('#1f2937', '#fff')} /> : r.expiringUnits ? <Chip size="small" label={`${r.expiringUnits} units`} sx={pill('#ffedd5', '#9a3412')} /> : dash,
      }),
      dateCol('lastSoldAt', 'Last Sold'),
      dateCol('lastReceivedAt', 'Last Received'),
    ],
  },
  {
    key: 'batches',
    label: 'Batches',
    defaultSort: 'received_desc',
    sorts: [['received_desc', 'Recently received'], ['value_desc', 'Value: high to low'], ['expiry_asc', 'Expiring soonest'], ['name_asc', 'Product: A to Z']],
    columns: [
      productCol,
      col('batchNo', 'Batch', { bold: true, text: (r) => r.batchNo }),
      col('warehouse', 'Warehouse', { text: (r) => r.warehouse }),
      col('racks', 'Rack Location', { text: (r) => r.racks, cell: (r) => r.racks || dash }),
      col('supplier', 'Supplier', { text: (r) => r.supplier, extra: (r) => ({ 'Invoice No.': r.invoiceNumber }) }),
      dateCol('receivedAt', 'Received'),
      num('received', 'Received Qty'),
      num('sold', 'Sold'),
      num('remaining', 'Remaining', { bold: true }),
      money('purchasePrice', 'Cost / Unit'),
      money('mrp', 'MRP'),
      money('costValue', 'Batch Value', { bold: true }),
      dateCol('expireDate', 'Expiry'),
      col('daysToExpiry', 'Expires In', { center: true, text: (r) => r.daysToExpiry ?? '', cell: (r) => expiryChip(r.daysToExpiry) }),
    ],
  },
  {
    key: 'expiry',
    label: 'Expiry Risk',
    defaultSort: 'expiry_asc',
    sorts: [['expiry_asc', 'Expiring soonest'], ['value_desc', 'Value: high to low']],
    columns: [
      productCol,
      col('batchNo', 'Batch', { bold: true, text: (r) => r.batchNo }),
      col('warehouse', 'Warehouse', { text: (r) => r.warehouse }),
      col('racks', 'Rack Location', { text: (r) => r.racks, cell: (r) => r.racks || dash }),
      num('remaining', 'Units at Risk', { bold: true }),
      money('costValue', 'Value at Risk', { bold: true }),
      money('mrpValue', 'MRP Value'),
      dateCol('expireDate', 'Expiry Date'),
      col('daysToExpiry', 'Expires In', { center: true, text: (r) => r.daysToExpiry, cell: (r) => expiryChip(r.daysToExpiry) }),
    ],
  },
  {
    key: 'dead',
    label: 'Dead Stock',
    defaultSort: 'value_desc',
    sorts: [['value_desc', 'Value: high to low'], ['idle_desc', 'Longest idle first']],
    columns: [
      productCol,
      col('category', 'Category', { text: (r) => r.category }),
      onHandCol,
      money('costValue', 'Stock Value (Cost)', { bold: true }),
      money('mrpValue', 'Value at MRP'),
      col('idleDays', 'Days Since Last Sale', {
        center: true,
        text: (r) => r.idleDays ?? 'Never sold',
        cell: (r) => (r.idleDays === null ? <Chip size="small" label="Never sold" sx={pill('#fee2e2', '#b91c1c')} /> : <Chip size="small" label={`${r.idleDays} days`} sx={pill('#ffedd5', '#9a3412')} />),
      }),
      dateCol('lastSoldAt', 'Last Sold'),
      dateCol('lastReceivedAt', 'Last Received'),
      col('expiring', 'Expiring', { center: true, text: (r) => r.expiringUnits + r.expiredUnits, cell: (r) => (r.expiringUnits + r.expiredUnits ? <Chip size="small" label={`${r.expiringUnits + r.expiredUnits} units`} sx={pill('#fee2e2', '#b91c1c')} /> : dash) }),
    ],
  },
  {
    key: 'reorder',
    label: 'Reorder List',
    defaultSort: 'urgency',
    sorts: [['urgency', 'Most urgent first'], ['reorder_desc', 'Largest reorder first']],
    columns: [
      productCol,
      col('status', 'Status', { text: (r) => STATUS[r.status]?.[2], cell: (r) => statusChip(r.status) }),
      onHandCol,
      num('minStock', 'Min Stock'),
      num('sold30', 'Sold (30d)'),
      coverCol,
      col('leadTime', 'Lead Time', { center: true, text: (r) => r.leadTime, cell: (r) => `${r.leadTime} d` }),
      num('suggestedReorder', 'Suggested Order Qty', {
        bold: true,
        cell: (r) => <Chip size="small" label={`${r.suggestedReorder} units`} sx={pill('#dbeafe', '#1d4ed8')} />,
      }),
      dateCol('lastReceivedAt', 'Last Received'),
    ],
  },
];

const InventoryReport = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const tab = TABS[tabIndex];
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [warehouse, setWarehouse] = useState('');
  const [category, setCategory] = useState('');
  const [brand, setBrand] = useState('');
  const [status, setStatus] = useState('');
  const [mismatch, setMismatch] = useState('');
  const [expiryDays, setExpiryDays] = useState('60');
  const [idleDays, setIdleDays] = useState('90');
  const [sortBy, setSortBy] = useState(TABS[0].defaultSort);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [notesOpen, setNotesOpen] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(id);
  }, [searchInput]);

  const filterParams = useMemo(() => {
    const p = { tab: tab.key, sortBy, expiryDays, idleDays };
    if (search) p.search = search;
    if (warehouse) p.warehouse = warehouse;
    if (category) p.category = category;
    if (brand) p.brand = brand;
    if (tab.key === 'summary') {
      if (status) p.status = status;
      if (mismatch) p.mismatch = mismatch;
    }
    return p;
  }, [tab.key, sortBy, expiryDays, idleDays, search, warehouse, category, brand, status, mismatch]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getData(REPORT_ROUTE, { ...filterParams, page: page + 1, limit: rowsPerPage }, authToken(), false);
      if (res?.success) setData(res);
      else setError(res?.message || 'Failed to load report');
    } catch (err) {
      setError(err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [filterParams, page, rowsPerPage]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const goToTab = (i, after) => {
    setTabIndex(i);
    setSortBy(TABS[i].defaultSort);
    setPage(0);
    if (after) after();
  };
  const setFilter = (setter) => (e) => {
    setter(e.target.value);
    setPage(0);
  };
  const summaryWith = (st, mm = '') => goToTab(0, () => {
    setStatus(st);
    setMismatch(mm);
  });
  const resetFilters = () => {
    setSearchInput('');
    setSearch('');
    setWarehouse('');
    setCategory('');
    setBrand('');
    setStatus('');
    setMismatch('');
    setExpiryDays('60');
    setIdleDays('90');
    setSortBy(tab.defaultSort);
    setPage(0);
  };

  const exportObjects = (rows) =>
    rows.map((r) => {
      const obj = {};
      for (const c of tab.columns) {
        obj[c.label] = c.text(r);
        if (c.extra) Object.assign(obj, c.extra(r));
      }
      return obj;
    });
  const fileStamp = () => `Inventory_${tab.label.replace(/[^A-Za-z]+/g, '_')}_${toISO(new Date())}`;

  const runExport = async (kind) => {
    setExporting(true);
    try {
      const res = await getData(REPORT_ROUTE, { ...filterParams, export: 'all' }, authToken(), false);
      if (!res?.success) throw new Error(res?.message || 'Export failed');
      const rows = res.rows || [];
      if (kind === 'csv') downloadCsv(exportObjects(rows), fileStamp());
      else if (kind === 'excel') downloadExcel(exportObjects(rows), fileStamp(), tab.label);
      else
        openPrintView({
          title: `Inventory Report — ${tab.label}`,
          subtitle: `As of ${fmtDate(new Date())} · ${rows.length} rows`,
          headers: tab.columns.map((c) => c.label),
          cells: rows.map((r) => tab.columns.map((c) => ({ value: c.print ? c.print(r) : c.text(r), className: c.numeric ? 'num' : '' }))),
        });
    } catch (err) {
      setError(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const stats = data?.stats || {};
  const total = data?.meta?.total || 0;
  const rows = data?.tab === tab.key ? data.rows || [] : [];
  const opts = data?.filterOptions || { warehouses: [], categories: [], brands: [] };

  const cards = [
    { label: 'Stock Value (Cost)', value: inr(stats.costValue), icon: MdCurrencyRupee, gradient: 'linear-gradient(135deg,#0ea5e9,#2563eb)', sub: `${inr(stats.mrpValue)} at MRP` },
    { label: 'Units On Hand', value: (stats.units || 0).toLocaleString('en-IN'), icon: MdInventory, gradient: 'linear-gradient(135deg,#6366f1,#8b5cf6)', sub: `${stats.skusInStock || 0} of ${stats.skus || 0} products in stock`, onClick: () => summaryWith(''), active: tab.key === 'summary' && !status && !mismatch },
    { label: 'Low Stock', value: (stats.low || 0).toLocaleString('en-IN'), icon: MdWarningAmber, gradient: 'linear-gradient(135deg,#f59e0b,#d97706)', sub: 'At or below minimum', onClick: () => summaryWith('low'), active: tab.key === 'summary' && status === 'low' },
    { label: 'Out of Stock', value: (stats.out || 0).toLocaleString('en-IN'), icon: MdRemoveShoppingCart, gradient: 'linear-gradient(135deg,#f43f5e,#dc2626)', sub: 'No batch stock left', onClick: () => summaryWith('out'), active: tab.key === 'summary' && status === 'out' },
    { label: 'Reorder Now', value: (stats.reorder || 0).toLocaleString('en-IN'), icon: MdAddShoppingCart, gradient: 'linear-gradient(135deg,#10b981,#059669)', sub: 'Products to reorder', onClick: () => goToTab(4), active: tab.key === 'reorder' },
    { label: `Expiring in ${stats.expiryDays || 60} days`, value: inr(stats.expiringValue), icon: MdEventBusy, gradient: 'linear-gradient(135deg,#be123c,#881337)', sub: `${inr(stats.expiredValue)} already expired · ${inr(stats.writtenOff30?.value)} written off (30d)`, onClick: () => goToTab(2), active: tab.key === 'expiry' },
    { label: 'Dead Stock', value: inr(stats.deadValue), icon: MdHotel, gradient: 'linear-gradient(135deg,#64748b,#334155)', sub: `${stats.deadCount || 0} products, no sale in ${stats.idleDays || 90}d`, onClick: () => goToTab(3), active: tab.key === 'dead' },
    { label: 'Stock Mismatch', value: (stats.mismatches || 0).toLocaleString('en-IN'), icon: MdSyncProblem, gradient: 'linear-gradient(135deg,#a855f7,#7e22ce)', sub: 'Storefront qty ≠ batch stock', onClick: () => summaryWith('', 'yes'), active: tab.key === 'summary' && mismatch === 'yes' },
  ];

  const activeFilterCount = [search, warehouse, category, brand, status, mismatch, sortBy !== tab.defaultSort].filter(Boolean).length;

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, fontFamily: FONT }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box>
          <Typography sx={{ fontSize: 23.5, fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>Inventory Report</Typography>
          <Typography sx={{ fontSize: 12.5, color: '#64748b', fontFamily: FONT }}>Live stock as of today — value, health, expiry risk and what to reorder</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button variant="outlined" startIcon={<MdInfoOutline />} onClick={() => setNotesOpen(true)} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', fontFamily: FONT }}>Note</Button>
          <Button startIcon={<MdFileDownload />} disabled={exporting || !total} onClick={() => runExport('csv')} sx={exportButtonSx('#16a34a', '#15803d')}>CSV</Button>
          <Button startIcon={<MdGridOn />} disabled={exporting || !total} onClick={() => runExport('excel')} sx={exportButtonSx('#0d9488', '#0f766e')}>Excel</Button>
          <Button startIcon={<MdPrint />} disabled={exporting || !total} onClick={() => runExport('print')} sx={exportButtonSx('#7c3aed', '#6d28d9')}>Print / PDF</Button>
        </Box>
      </Box>

      <Box sx={statGridSx}>
        {cards.map((c) => (
          <StatCard key={c.label} {...c} />
        ))}
      </Box>

      {stats.mismatches > 0 && !warehouse && (
        <Alert severity="warning" sx={{ mb: 2, fontFamily: FONT }} action={<Button color="inherit" size="small" onClick={() => summaryWith('', 'yes')}>Show</Button>}>
          {stats.mismatches} product(s) have a storefront stock count that doesn't match their batch stock — customers may be able to order stock you don't have, or be blocked from stock you do.
        </Alert>
      )}

      <Paper elevation={0} sx={{ p: 2.5, borderRadius: '16px', mb: 3, border: '1px solid #e2e8f0' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography sx={{ fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>
            Filters {activeFilterCount > 0 && <Chip size="small" label={`${activeFilterCount} active`} color="primary" sx={{ ml: 1, fontWeight: 700 }} />}
          </Typography>
          <Button size="small" startIcon={<MdRefresh />} onClick={resetFilters} sx={{ textTransform: 'none', fontWeight: 700, color: '#dc2626' }}>Reset</Button>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={6} lg={3}>
            <TextField
              label="Search product name / code"
              size="small"
              fullWidth
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              InputProps={{ startAdornment: (<InputAdornment position="start"><MdSearch size={18} /></InputAdornment>) }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2.25}>
            <TextField select label="Warehouse" size="small" fullWidth value={warehouse} onChange={setFilter(setWarehouse)}>
              <MenuItem value="">All warehouses</MenuItem>
              {opts.warehouses.map((w) => (
                <MenuItem key={w.value} value={w.value}>{w.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2.25}>
            <TextField select label="Category" size="small" fullWidth value={category} onChange={setFilter(setCategory)} SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 320 } } } }}>
              <MenuItem value="">All categories</MenuItem>
              {opts.categories.map((c) => (
                <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2.25}>
            <TextField select label="Brand" size="small" fullWidth value={brand} onChange={setFilter(setBrand)} SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 320 } } } }}>
              <MenuItem value="">All brands</MenuItem>
              {opts.brands.map((b) => (
                <MenuItem key={b} value={b}>{b}</MenuItem>
              ))}
            </TextField>
          </Grid>
          {tab.key === 'summary' && (
            <>
              <Grid item xs={12} sm={6} md={3} lg={2.25}>
                <TextField select label="Stock status" size="small" fullWidth value={status} onChange={setFilter(setStatus)}>
                  <MenuItem value="">All</MenuItem>
                  {Object.entries(STATUS).map(([k, v]) => (
                    <MenuItem key={k} value={k}>{v[2]}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={2.25}>
                <TextField select label="Stock mismatch" size="small" fullWidth value={mismatch} onChange={setFilter(setMismatch)}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="yes">Only mismatched</MenuItem>
                </TextField>
              </Grid>
            </>
          )}
          {(tab.key === 'expiry' || tab.key === 'summary') && (
            <Grid item xs={12} sm={6} md={3} lg={2.25}>
              <TextField select label="Expiring within" size="small" fullWidth value={expiryDays} onChange={setFilter(setExpiryDays)}>
                {['30', '60', '90', '180'].map((d) => (
                  <MenuItem key={d} value={d}>{d} days</MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
          {tab.key === 'dead' && (
            <Grid item xs={12} sm={6} md={3} lg={2.25}>
              <TextField select label="No sale for" size="small" fullWidth value={idleDays} onChange={setFilter(setIdleDays)}>
                {['30', '60', '90', '180', '365'].map((d) => (
                  <MenuItem key={d} value={d}>{d}+ days</MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={3} lg={2.25}>
            <TextField select label="Sort by" size="small" fullWidth value={sortBy} onChange={setFilter(setSortBy)}>
              {tab.sorts.map(([v, l]) => (
                <MenuItem key={v} value={v}>{l}</MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <Tabs
          value={tabIndex}
          onChange={(_, v) => goToTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 1,
            borderBottom: '1px solid #e2e8f0',
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, fontFamily: FONT, minHeight: 56 },
            '& .Mui-selected': { color: '#0f172a !important' },
            '& .MuiTabs-indicator': { background: 'rgb(255 209 2)', height: 4, borderRadius: 2 },
          }}
        >
          {TABS.map((t) => (
            <Tab key={t.key} label={t.label} />
          ))}
        </Tabs>
        <Box sx={{ px: 2.5, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <Typography sx={{ fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>{tab.label}</Typography>
          <Typography sx={{ fontSize: 11.5, color: '#64748b', fontFamily: FONT }}>{total.toLocaleString('en-IN')} rows</Typography>
        </Box>
        {(loading || exporting) && <LinearProgress />}
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {tab.columns.map((c) => (
                  <TableCell key={c.key} align={c.center ? 'center' : c.numeric ? 'right' : 'left'} sx={{ fontWeight: 800, fontFamily: FONT, background: '#0f172a', color: '#fff', whiteSpace: 'nowrap', py: 1.5 }}>
                    {c.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={tab.columns.length} sx={{ textAlign: 'center', color: '#94a3b8', py: 6, fontFamily: FONT }}>
                    {tab.key === 'reorder' ? 'Nothing needs reordering right now.' : 'Nothing matches the selected filters.'}
                  </TableCell>
                </TableRow>
              )}
              {rows.map((r, idx) => (
                <TableRow
                  key={idx}
                  hover
                  sx={{
                    '&:nth-of-type(even)': { background: '#f8fafc' },
                    boxShadow: r.status === 'out' || (r.daysToExpiry !== undefined && r.daysToExpiry !== null && r.daysToExpiry < 0) || r.mismatch ? 'inset 4px 0 0 #dc2626' : 'none',
                  }}
                >
                  {tab.columns.map((c) => (
                    <TableCell key={c.key} align={c.center ? 'center' : c.numeric ? 'right' : 'left'} sx={{ fontFamily: FONT, whiteSpace: 'nowrap', fontSize: 11.5, fontWeight: c.bold ? 700 : 500, color: '#334155' }}>
                      {c.cell ? c.cell(r) : c.text(r)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={Math.min(page, Math.max(Math.ceil(total / rowsPerPage) - 1, 0))}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid #e2e8f0' }}
        />
      </Paper>

      <Dialog open={notesOpen} onClose={() => setNotesOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, fontFamily: FONT }}>Note</DialogTitle>
        <DialogContent dividers>
          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            {(data?.notes || []).map((n, i) => (
              <Typography component="li" key={i} variant="body2" sx={{ mb: 1, fontFamily: FONT }}>{n}</Typography>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InventoryReport;
